import { Component, forwardRef, Input, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { marked } from 'marked';
import TurndownService from 'turndown';

@Component({
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  styleUrl: './editor.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditorComponent),
      multi: true
    }
  ]
})
export class EditorComponent implements ControlValueAccessor {
  @Input() placeholder: string = 'Enter text...';

  value: string = '';  // HTML value for the editor
  currentMarkdown: string = '';  // Track current markdown to prevent loops
  disabled: boolean = false;
  onChange: any = () => {};
  onTouched: any = () => {};

  private turndownService: TurndownService;
  private isUpdatingFromMarkdown: boolean = false;

  modules = {
    toolbar: [
      ['bold', 'italic'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link']
    ]
  };

  // Restrict allowed formats to prevent images and other unwanted content
  formats = ['bold', 'italic', 'list', 'link'];

  editorStyles = {
    'min-height': '200px'
  };

  constructor() {
    // Initialize Turndown service with options
    this.turndownService = new TurndownService({
      headingStyle: 'atx',
      bulletListMarker: '-',
      codeBlockStyle: 'fenced',
      emDelimiter: '_'
    });

    // Configure marked options
    marked.setOptions({
      breaks: true,  // Convert line breaks to <br>
      gfm: true      // Use GitHub Flavored Markdown
    });
  }

  writeValue(markdown: any): void {
    if (!markdown) {
      this.value = '';
      this.currentMarkdown = '';
      return;
    }

    // Check if the incoming markdown is different from current
    if (markdown === this.currentMarkdown) {
      return; // Prevent loop
    }

    this.currentMarkdown = markdown;
    this.isUpdatingFromMarkdown = true;

    // Convert markdown to HTML for the editor
    try {
      const html = marked(markdown) as string;
      this.value = this.normalizeLinks(html);
    } catch (error) {
      console.error('Error converting markdown to HTML:', error);
      this.value = markdown; // Fallback to raw markdown if conversion fails
    }

    // Reset flag after a brief delay to ensure editor has updated
    setTimeout(() => {
      this.isUpdatingFromMarkdown = false;
    }, 100);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onContentChanged(event: any): void {
    // Skip if this change is from markdown update
    if (this.isUpdatingFromMarkdown) {
      return;
    }

    // The onContentChanged event provides an object with html, text, content, etc.
    if (event && event.html !== undefined) {
      // Normalize links before processing
      const normalizedHtml = this.normalizeLinks(event.html);
      this.value = normalizedHtml;

      // Convert HTML to markdown
      try {
        const markdown = this.turndownService.turndown(normalizedHtml || '');
        this.currentMarkdown = markdown;
        this.onChange(markdown);
        this.onTouched();
      } catch (error) {
        console.error('Error converting HTML to markdown:', error);
        this.onChange(normalizedHtml); // Fallback to HTML if conversion fails
        this.onTouched(); // Mark as touched even on error
      }
    }
  }

  private normalizeLinks(html: string): string {
    if (!html || html.trim() === '') {
      return html;
    }

    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find all anchor tags
    const anchors = tempDiv.querySelectorAll('a');
    anchors.forEach(anchor => {
      const href = anchor.getAttribute('href');
      if (href) {
        // Check if href already has a protocol (http://, https://, mailto:, tel:, ftp://, etc.)
        const hasProtocol = /^([a-zA-Z][a-zA-Z\d+\-.]*:)/i.test(href);

        // If no protocol and not a relative path starting with / or #, add https://
        if (!hasProtocol && !href.startsWith('/') && !href.startsWith('#')) {
          anchor.setAttribute('href', 'https://' + href);
        }
      }
    });

    return tempDiv.innerHTML;
  }
}
